import * as DropdownPrimitive from "@radix-ui/react-dropdown-menu";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import { UserTag } from "@app/shared/components/UserTag";
import { usersAtom } from "@app/store/userStore";
import { useAtomValue } from "jotai";
import { User } from "../../../../../generated/client/graphql";
import ButtonRegular from "../../../Button/ButtonRegular";
import { AlertModal, AlertModalContent } from "../../../Modal/AlertModal";
import UserDropdown from "./dropdown";
import "./i18n";

interface UserSelectProps {
    selectedUserId?: string;
    selectedUserIds?: string[];
    onChange: (userId: string | undefined) => void;
    userIdsToInclude?: string[];
    title?: string;
    size?: "small" | "regular";
    disabled?: boolean;
}

export default function UserSelect({
    selectedUserId,
    selectedUserIds,
    onChange,
    userIdsToInclude,
    disabled = false,
}: UserSelectProps) {
    const { data } = useAtomValue(usersAtom);
    const { t } = useTranslation("UserDropdown");

    const [selectOpen, setSelectOpen] = useState(false);
    const [alertOpen, setAlertOpen] = useState(false);
    const [toConfirmUser, setToConfirmUser] = useState<User | undefined>();
    const onUserClick = (newUserId: string | undefined) => {
        if (selectedUserIds && selectedUserIds.length > 1) {
            setSelectOpen(false);
            setAlertOpen(true);
            setToConfirmUser(
                data?.users.find(({ id }) => id === newUserId) as
                    | User
                    | undefined,
            );
        } else {
            onChange(newUserId);
        }
    };
    return (
        <>
            <DropdownPrimitive.Root
                open={selectOpen}
                onOpenChange={setSelectOpen}
            >
                <DropdownPrimitive.Trigger asChild disabled={disabled}>
                    <UserTag
                        userId={selectedUserId}
                        size="sm"
                        displayFullName
                        noUserText={t("unassigned")}
                        isTrigger
                    />
                </DropdownPrimitive.Trigger>
                <UserDropdown
                    users={
                        (data?.users.filter(
                            (u) =>
                                userIdsToInclude === undefined ||
                                userIdsToInclude.includes(u.id),
                        ) || []) as User[]
                    }
                    onUserIdChange={onUserClick}
                />
            </DropdownPrimitive.Root>
            <AlertModal
                open={alertOpen}
                onOpenChange={(o) => {
                    setAlertOpen(o);
                    setSelectOpen(o);
                }}
            >
                <AlertModalContent
                    title={t("Replace users")}
                    actions={
                        <ButtonRegular
                            text={t("replace")}
                            onClick={() => {
                                onChange(toConfirmUser?.id);
                                setAlertOpen(false);
                                setSelectOpen(false);
                            }}
                        />
                    }
                >
                    {t("replace_confirm", {
                        count: selectedUserIds?.length,
                        user: `${toConfirmUser?.firstName} ${toConfirmUser?.lastName}`,
                    })}
                </AlertModalContent>
            </AlertModal>
        </>
    );
}
